import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Shield } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';

const blogPosts = [
  {
    slug: 'automate-financial-reconciliation',
    title: 'How to Automate Financial Reconciliation in 2025',
    excerpt: 'Learn how modern AI-powered tools are transforming financial reconciliation from a time-consuming manual process to an automated, accurate system.',
    date: '2025-11-15',
    readTime: '8 min read',
    category: 'Automation',
    image: '/placeholder.svg'
  },
  {
    slug: 'duplicate-invoice-detection',
    title: 'The Complete Guide to Duplicate Invoice Detection',
    excerpt: 'Discover advanced techniques and best practices for detecting and preventing duplicate invoice payments that cost businesses millions annually.',
    date: '2025-11-10',
    readTime: '10 min read',
    category: 'Fraud Prevention',
    image: '/placeholder.svg'
  },
  {
    slug: 'ai-financial-security',
    title: 'AI-Powered Financial Security: The Future is Here',
    excerpt: 'Explore how artificial intelligence is revolutionizing financial security with real-time fraud detection, risk scoring, and automated compliance.',
    date: '2025-11-05',
    readTime: '12 min read',
    category: 'AI & Technology',
    image: '/placeholder.svg'
  }
];

export default function Blog() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Sentinel Financial Intelligence Blog",
    "description": "Expert insights on financial reconciliation, fraud detection, and AI-powered financial technology",
    "url": "https://getsentinel.pro/blog",
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "url": `https://getsentinel.pro/blog/${post.slug}`,
      "author": {
        "@type": "Organization",
        "name": "Sentinel"
      }
    }))
  };

  return (
    <>
      <SEO 
        title="Blog - Financial Intelligence Insights | Sentinel"
        description="Expert insights on financial reconciliation, duplicate invoice detection, AI-powered fraud prevention, and modern financial technology solutions."
        canonical="https://yoursite.lovable.app/blog"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-primary rounded-2xl flex items-center justify-center shadow-glow">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight">SENTINEL</span>
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost">Home</Button>
              </Link>
              <Link to="/auth">
                <Button>Get Started</Button>
              </Link>
            </nav>
          </div>
        </header>

        {/* Hero */}
        <section className="py-20 bg-gradient-to-b from-surface/30 to-background">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent">
              Financial Intelligence Insights
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Expert perspectives on financial reconciliation, fraud detection, and AI-powered financial technology
            </p>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <main className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <article key={post.slug}>
                  <Card className="h-full border-border/50 overflow-hidden hover:border-primary/30 transition-all duration-300 hover:shadow-lg group">
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="aspect-video bg-surface/50 overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      
                      <div className="p-6 space-y-4">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">
                            {post.category}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>

                        <h2 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>

                        <p className="text-muted-foreground line-clamp-3">
                          {post.excerpt}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-border/50">
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                          <span className="text-primary font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                            Read more <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </Card>
                </article>
              ))}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
